import { put, call, all, takeEvery, takeLatest } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import {
  list as aiAgentsBuilderList,
  get as aiAgentBuilderGet,
  add as aiAgentBuilderAdd,
  update as aiAgentBuilderUpdate,
  remove as aiAgentBuilderRemove,
  AgentListResponse,
  AgentMutationResponse,
  AgentPayload,
  AiAgent,
  ChatApp,
} from 'services/agentBuilder'
import type { ChatApiErrorBody } from 'services/agentApi'
import appsActions from 'redux/apps/actions'
import actions from './actions'
import { AiAgentsState } from './reducers'

/**
 * AI Agents Builder sagas — the 5 handlers the AI Agents list page dispatches.
 * The frozen sidebar still dispatches their `apps/*` twins, so behaviour must match.
 */

type Saga<P> = { payload: P }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SagaWorker = (action: any) => any

interface RootState {
  apps: {
    active: ChatApp
    appSettings: unknown
    aiAgentsBuilderList?: AiAgent[]
    activeBuilderAgent?: AiAgent | null
  }
  aiAgents: AiAgentsState
}
const getRoot = (): RootState => reduxStore.getState() as unknown as RootState

/**
 * The agent-config sidebar and bootstrap still read the `apps` copy, so each mutation here
 * mirrors onto it the way the legacy `apps/*` handlers did.
 */
function* syncAppsCopy(update: (apps: RootState['apps']) => Partial<RootState['apps']>) {
  yield put({ type: appsActions.SET_STATE, payload: update(getRoot().apps) })
}

const notify = (msg: string, type: 'success' | 'error') =>
  window.triggerNotification?.(msg, type)

type AgentApiError = { response?: { data?: { error?: ChatApiErrorBody } } }

/** The agent API rejects on failure, so the code rides on the thrown error, not on a resolved body. */
const createAgentErrorMessage = (error: unknown): string => {
  const body = (error as AgentApiError)?.response?.data?.error
  if (body?.code === 'ERR_PLAN_RESTRICTION') return 'Plan restriction - Feature not available'
  if (body?.code === 'ERR_BAD_REQUEST') {
    return body.details?.join(', ') || body.message || 'Failed to create AI Agent'
  }
  return 'Failed to create AI Agent'
}

interface ListPayload {
  data?: ChatApp
  searchQuery?: string
  /**
   * `objectToParams(meta.next | meta.previous)` output. The caller owns the cursor
   * because `meta.current` carries no page index.
   */
  cursorParams?: string
}

export function* AI_AGENTS_BUILDER_LIST({ payload }: Saga<ListPayload>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areAiAgentsBuildersLoading: true } })

    const app = payload.data ?? getRoot().apps.active

    const success: AgentListResponse = yield call(aiAgentsBuilderList, app, {
      search: payload.searchQuery || '',
      cursorParams: payload.cursorParams || '',
    })

    yield put({
      type: actions.SET_STATE,
      payload: {
        aiAgentsBuilderList: success?.data ?? [],
        aiAgentsBuilderListPagination: success?.meta ?? {},
        areAiAgentsBuildersLoading: false,
      },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        aiAgentsBuilderList: [],
        aiAgentsBuilderListPagination: {},
        areAiAgentsBuildersLoading: false,
      },
    })
    notify('Failed to fetch AI Agents', 'error')
  }
}

interface GetPayload {
  data?: ChatApp
  agentId: string
}

export function* GET_AI_AGENT_BUILDER({ payload }: Saga<GetPayload>) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: { isActiveBuilderAgentLoading: true, activeBuilderAgent: null },
    })

    const app = payload.data ?? getRoot().apps.active
    const success: AiAgent = yield call(aiAgentBuilderGet, app, payload.agentId)

    yield put({
      type: actions.SET_STATE,
      payload: { activeBuilderAgent: success || null, isActiveBuilderAgentLoading: false },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { activeBuilderAgent: null, isActiveBuilderAgentLoading: false },
    })
  }
}

interface AddPayload {
  values: AgentPayload
  data?: ChatApp
  /** `false` keeps the caller in charge of navigation. */
  redirect?: boolean
}

export function* ADD_AI_AGENT_BUILDER({ payload }: Saga<AddPayload>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { isAiAgentBuilderCreateFormLoading: true } })

    const app = payload.data ?? getRoot().apps.active
    const success: AgentMutationResponse = yield call(aiAgentBuilderAdd, app, payload.values)

    if (success?.agent) {
      yield put({ type: actions.AI_AGENTS_BUILDER_LIST, payload: { data: app, cursorParams: '' } })
      // An emptied list is what makes the sidebar refetch, picking up the new agent.
      yield* syncAppsCopy(() => ({ aiAgentsBuilderList: [] }))
      yield put({
        type: actions.SET_STATE,
        payload: {
          isAiAgentBuilderCreateFormLoading: false,
          lastCreatedAgentId: success.agent.uid ?? null,
        },
      })
      notify('AI Agent created successfully', 'success')

      if (payload.redirect !== false && app?.id) {
        yield put(push(`/app/${app.id}/ai-agents/${success.agent.uid}/instructions`))
      }
      return
    }

    yield put({ type: actions.SET_STATE, payload: { isAiAgentBuilderCreateFormLoading: false } })
    notify('Failed to create AI Agent', 'error')
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { isAiAgentBuilderCreateFormLoading: false } })
    notify(createAgentErrorMessage(error), 'error')
  }
}

interface DeletePayload {
  data?: ChatApp
  agentId: string
  onSuccess?: () => void
}

export function* DELETE_AI_AGENT_BUILDER({ payload }: Saga<DeletePayload>) {
  const app = payload.data ?? getRoot().apps.active
  const withoutAgent = (ids: string[] = []) => ids.filter(id => id !== payload.agentId)

  yield put({
    type: actions.SET_STATE,
    payload: {
      deletingAgentBuilderIds: [
        ...(getRoot().aiAgents.deletingAgentBuilderIds || []),
        payload.agentId,
      ],
    },
  })

  try {
    yield call(aiAgentBuilderRemove, app, payload.agentId)

    yield put({
      type: actions.SET_STATE,
      payload: {
        deletingAgentBuilderIds: withoutAgent(getRoot().aiAgents.deletingAgentBuilderIds),
      },
    })
    yield* syncAppsCopy(apps => ({
      aiAgentsBuilderList: (apps.aiAgentsBuilderList ?? []).filter(
        agent => agent.uid !== payload.agentId,
      ),
      activeBuilderAgent:
        apps.activeBuilderAgent?.uid === payload.agentId ? null : apps.activeBuilderAgent,
    }))
    notify('AI Agent deleted successfully', 'success')

    if (typeof payload.onSuccess === 'function') payload.onSuccess()
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        deletingAgentBuilderIds: withoutAgent(getRoot().aiAgents.deletingAgentBuilderIds),
      },
    })
    notify('Failed to delete AI Agent', 'error')
  }
}

interface EditPayload {
  data?: ChatApp
  agentId: string
  agentData: Partial<AgentPayload> & { isActive?: boolean }
  /** Lets the caller re-read the page it is showing; a blind refetch would snap it to page 1. */
  onError?: () => void
}

export function* EDIT_AI_AGENT_BUILDER({ payload }: Saga<EditPayload>) {
  const app = payload.data ?? getRoot().apps.active
  const withoutAgent = (ids: string[] = []) => ids.filter(id => id !== payload.agentId)

  yield put({
    type: actions.SET_STATE,
    payload: {
      updatingAgentBuilderIds: [
        ...(getRoot().aiAgents.updatingAgentBuilderIds || []),
        payload.agentId,
      ],
    },
  })

  try {
    yield call(aiAgentBuilderUpdate, app, payload.agentId, payload.agentData)

    const state = getRoot().aiAgents
    const active = state.activeBuilderAgent
    yield put({
      type: actions.SET_STATE,
      payload: {
        aiAgentsBuilderList: state.aiAgentsBuilderList.map(agent =>
          agent.uid === payload.agentId ? { ...agent, ...payload.agentData } : agent,
        ),
        updatingAgentBuilderIds: withoutAgent(state.updatingAgentBuilderIds),
        activeBuilderAgent:
          active && active.uid === payload.agentId ? { ...active, ...payload.agentData } : active,
      },
    })
    yield* syncAppsCopy(apps => ({
      aiAgentsBuilderList: (apps.aiAgentsBuilderList ?? []).map(agent =>
        agent.uid === payload.agentId ? { ...agent, ...payload.agentData } : agent,
      ),
      activeBuilderAgent:
        apps.activeBuilderAgent?.uid === payload.agentId
          ? { ...apps.activeBuilderAgent, ...payload.agentData }
          : apps.activeBuilderAgent,
    }))
    notify('AI Agent updated successfully', 'success')
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        updatingAgentBuilderIds: withoutAgent(getRoot().aiAgents.updatingAgentBuilderIds),
      },
    })
    // Refetch rather than reverse the optimistic patch, so the list can't drift.
    if (typeof payload.onError === 'function') payload.onError()
    else yield put({ type: actions.AI_AGENTS_BUILDER_LIST, payload: { data: app } })
    notify('Failed to update AI Agent', 'error')
  }
}

export default function* aiAgentsBuildersSaga() {
  yield all([
    // takeLatest, not takeEvery: this worker is fed by the debounced search box, so a
    // slow earlier response must not overwrite a newer one.
    takeLatest(actions.AI_AGENTS_BUILDER_LIST, AI_AGENTS_BUILDER_LIST as SagaWorker),
    takeEvery(actions.GET_AI_AGENT_BUILDER, GET_AI_AGENT_BUILDER as SagaWorker),
    takeEvery(actions.ADD_AI_AGENT_BUILDER, ADD_AI_AGENT_BUILDER as SagaWorker),
    takeEvery(actions.DELETE_AI_AGENT_BUILDER, DELETE_AI_AGENT_BUILDER as SagaWorker),
    takeEvery(actions.EDIT_AI_AGENT_BUILDER, EDIT_AI_AGENT_BUILDER as SagaWorker),
  ])
}
